import { formatCents, parseMoneyToCents } from '@munifocus/billing';
import type { StatusKind } from '@munifocus/ui';
import type { CostItemStatus, CostRecoveryStatus } from '@munifocus/contracts-gov';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * Presentation for cost recovery: money and dates in the reader's language,
 * and the chip each state wears. Arithmetic lives in contracts-gov; nothing
 * here computes an amount, it only reads and prints one.
 */

const localeOf = (language: string) => (language.startsWith('fr') ? 'fr-CA' : 'en-CA');

export function money(cents: number, language: string): string {
  return formatCents(cents, 'CAD', localeOf(language));
}

/** Typed dollars to cents in the reader's convention, or null when ambiguous. */
export function parseMoney(text: string, language: string): number | null {
  return parseMoneyToCents(text, localeOf(language));
}

/**
 * Hours as typed — "2.25", "2,25", "3" — to hundredths of an hour. At most two
 * decimals and at most 999 hours; anything else is null rather than a guess.
 */
export function parseHours(text: string): number | null {
  const normalised = text.trim().replace(',', '.');
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(normalised)) return null;
  const hundredths = Math.round(Number(normalised) * 100);
  return hundredths > 0 ? hundredths : null;
}

export function hoursLabel(hundredths: number, language: string): string {
  return new Intl.NumberFormat(localeOf(language), { maximumFractionDigits: 2 }).format(
    hundredths / 100,
  );
}

/** An ISO calendar day, printed. Formatted in UTC so it never slips a day. */
export function day(iso: string | null, language: string): string {
  if (!iso) return '—';
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(localeOf(language), {
      dateStyle: 'medium',
      timeZone: 'UTC',
    }).format(new Date(`${iso}T00:00:00Z`)),
  );
}

export function rate(bps: number, language: string): string {
  return new Intl.NumberFormat(localeOf(language), {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(bps / 10_000);
}

export const RECOVERY_STATUS_KIND: Record<CostRecoveryStatus, StatusKind> = {
  open: 'neutral',
  issued: 'pending',
  payment_plan: 'approved',
  overdue: 'warning',
  escalated: 'emergency',
  paid: 'success',
};

export const ITEM_STATUS_KIND: Record<CostItemStatus, StatusKind> = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  void: 'neutral',
};

/**
 * Today in the enforcement calendar (America/Moncton), as an ISO day — the
 * default and the upper bound for a date field.
 *
 * The API refuses a cost or payment dated after *its* today, which it reckons
 * in Moncton. Using the browser's own calendar let a viewer whose clock runs
 * ahead of Atlantic time (a UTC CI runner, a travelling laptop) default the
 * field to a day the server calls the future, and the save was refused.
 */
export function todayIso(now: Date = new Date()): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/Moncton',
    }).format(now),
  );
}
